import { useEffect, useRef, useState, useCallback } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { masterDb } from '../firebase/master';
import { useAuth } from '../context/AuthContext';

const IDLE_WARN_MS  = 25 * 60 * 1000; // warn at 25 min
const IDLE_OUT_MS   = 30 * 60 * 1000; // logout at 30 min
const HEARTBEAT_MS  =  5 * 60 * 1000; // update lastActiveAt every 5 min

export function useActivity() {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning]   = useState(false);
  const [secondsLeft, setSecondsLeft]   = useState(300);

  const warnTimer      = useRef<ReturnType<typeof setTimeout>>(undefined);
  const outTimer       = useRef<ReturnType<typeof setTimeout>>(undefined);
  const heartbeat      = useRef<ReturnType<typeof setInterval>>(undefined);
  const countdown      = useRef<ReturnType<typeof setInterval>>(undefined);

  const sendHeartbeat = useCallback(async () => {
    if (!user?.id) return;
    try {
      await updateDoc(doc(masterDb, 'sessions', user.id), {
        lastActiveAt: serverTimestamp(),
      });
    } catch { /* session may be gone */ }
  }, [user?.id]);

  const resetTimers = useCallback(() => {
    setShowWarning(false);
    clearTimeout(warnTimer.current);
    clearTimeout(outTimer.current);
    clearInterval(countdown.current);

    warnTimer.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(300);
      countdown.current = setInterval(() => {
        setSecondsLeft(s => {
          if (s <= 1) clearInterval(countdown.current);
          return s - 1;
        });
      }, 1000);
    }, IDLE_WARN_MS);

    outTimer.current = setTimeout(() => {
      logout();
    }, IDLE_OUT_MS);
  }, [logout]);

  useEffect(() => {
    if (!user) return;

    resetTimers();

    heartbeat.current = setInterval(sendHeartbeat, HEARTBEAT_MS);

    const EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'] as const;
    const onActivity = () => resetTimers();
    EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    return () => {
      clearTimeout(warnTimer.current);
      clearTimeout(outTimer.current);
      clearInterval(heartbeat.current);
      clearInterval(countdown.current);
      EVENTS.forEach(e => window.removeEventListener(e, onActivity));
    };
  }, [user, resetTimers, sendHeartbeat]);

  return { showWarning, secondsLeft, stayLoggedIn: resetTimers };
}
